import { useState, useEffect, useCallback } from 'react'
import { patternPipelineApi, type PatternData } from '@/services/api/patternPipeline'
import { Card, LoadingSpinner, Badge } from '@/components/common'
import { Search, Filter } from 'lucide-react'

const FIELD_LABELS: Record<string, string> = {
  room: 'Room',
  class_name: 'Class',
  subject: 'Subject',
  teacher_name: 'Teacher Name',
  teacher_code: 'Teacher Code',
  first_name: 'First Name',
  family_name: 'Family Name',
  time_slot: 'Time Slot',
  ignore: 'Ignore',
}

interface PatternEntry {
  regex: string
  field: string
  match_mode: string
  flags: string[]
  priority: number
  source: string
}

export function UltraadminGlobalPatternsPage() {
  const [data, setData] = useState<PatternData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [fieldFilter, setFieldFilter] = useState('')
  const [expandedSection, setExpandedSection] = useState<string>('single')

  const loadData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await patternPipelineApi.getGlobal()
      setData(result)
    } catch (err: any) {
      setError(err.message || 'Failed to load global patterns')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadData()
  }, [loadData])

  const filterPatterns = (patterns: PatternEntry[]) => {
    return patterns.filter((p) => {
      if (fieldFilter && p.field !== fieldFilter) return false
      if (search) {
        const s = search.toLowerCase()
        return p.regex.toLowerCase().includes(s) || p.field.toLowerCase().includes(s) || p.source.toLowerCase().includes(s)
      }
      return true
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        <Card className="p-8 text-center text-gray-500">No data available</Card>
      </div>
    )
  }

  const allFields = [...new Set([...data.single, ...data.multi, ...data.orphan].map((p) => p.field))].sort()

  const sections: { key: string; label: string; patterns: PatternEntry[] }[] = [
    { key: 'single', label: 'Single Token Patterns', patterns: filterPatterns(data.single) },
    { key: 'multi', label: 'Multi-Token Patterns', patterns: filterPatterns(data.multi) },
    { key: 'orphan', label: 'Orphan Detection Patterns', patterns: filterPatterns(data.orphan) },
  ]

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Global Pattern Base</h1>
        <p className="mt-1 text-gray-500">
          Read-only view of all global patterns. Total: {data.single.length + data.multi.length + data.orphan.length} patterns, {data.ignore_tokens.length} ignore tokens
        </p>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Filters */}
      <Card className="p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search patterns..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={fieldFilter}
              onChange={(e) => setFieldFilter(e.target.value)}
            >
              <option value="">All fields</option>
              {allFields.map((f) => (
                <option key={f} value={f}>{FIELD_LABELS[f] || f}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Pattern Sections */}
      {sections.map((section) => (
        <Card key={section.key} className="mb-4">
          <button
            className="w-full p-4 flex items-center justify-between text-left"
            onClick={() => setExpandedSection(expandedSection === section.key ? '' : section.key)}
          >
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{section.label}</h2>
              <p className="text-sm text-gray-500">{section.patterns.length} pattern{section.patterns.length !== 1 ? 's' : ''}</p>
            </div>
            <span className="text-gray-400">{expandedSection === section.key ? '▼' : '▶'}</span>
          </button>

          {expandedSection === section.key && (
            <div className="border-t">
              {section.patterns.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">No patterns match your filters</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-2 px-4 font-medium text-gray-600">Regex</th>
                        <th className="text-left py-2 px-4 font-medium text-gray-600">Field</th>
                        <th className="text-left py-2 px-4 font-medium text-gray-600">Mode</th>
                        <th className="text-left py-2 px-4 font-medium text-gray-600">Priority</th>
                        <th className="text-left py-2 px-4 font-medium text-gray-600">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.patterns.map((p, i) => (
                        <tr key={i} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-4 font-mono text-xs truncate max-w-[300px]" title={p.regex}>
                            {p.regex}
                          </td>
                          <td className="py-2 px-4">
                            <Badge variant="default">{FIELD_LABELS[p.field] || p.field}</Badge>
                          </td>
                          <td className="py-2 px-4 text-gray-600">{p.match_mode}</td>
                          <td className="py-2 px-4 text-gray-600">{p.priority}</td>
                          <td className="py-2 px-4 text-gray-600">{p.source}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Card>
      ))}

      {/* Ignore Tokens */}
      <Card className="p-4">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Ignore Tokens ({data.ignore_tokens.length})</h2>
        <div className="flex flex-wrap gap-2">
          {data.ignore_tokens.sort().map((token) => (
            <span key={token} className="px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-mono">
              {token}
            </span>
          ))}
        </div>
      </Card>
    </div>
  )
}
